import { laborPageKeysEquivalent } from "@/pages/clampqc/clampQcLaborClockState";

import type { LaborActiveSessionPayload } from "./laborApi";
import type { LaborDomain } from "./laborTarget";
import { isLaborOwnerDeparting } from "./laborOwnerDeparture";
import { waitForLaborOwnerDepartureComplete } from "./laborOwnerDepartureWait";

export function activeOnOtherPageFromPayload(payload: LaborActiveSessionPayload): string {
  return typeof payload.active_on_other_page === "string" ? payload.active_on_other_page.trim() : "";
}

/**
 * True when `active_on_other_page` should not surface yet — the owning tab is still ending
 * the previous session, or the conflict is this tab's own open row on a different job key.
 */
export function shouldDeferActiveOnOtherConflict(input: {
  activeOnOtherPage: string;
  ownerDeparting: boolean;
  openSessionPage: string | null;
  openSessionTabId: string | null;
  currentTabId: string;
}): boolean {
  if (input.ownerDeparting) {
    return true;
  }
  const otherPage = input.activeOnOtherPage.trim();
  if (otherPage === "") {
    return false;
  }
  const openPage = (input.openSessionPage ?? "").trim();
  if (openPage === "" || !laborPageKeysEquivalent(otherPage, openPage)) {
    return false;
  }
  const sessionTabId = input.openSessionTabId?.trim() ?? "";
  if (sessionTabId === "" || sessionTabId !== input.currentTabId.trim()) {
    return false;
  }
  return true;
}

export async function fetchLaborActiveSessionResolvingDeparture(input: {
  page: string;
  domain: LaborDomain;
  fetchActiveSession: (input: {
    page: string;
    domain?: LaborDomain;
  }) => Promise<LaborActiveSessionPayload>;
  currentTabId: string;
  openSessionPage: string | null;
  openSessionTabId: string | null;
  maxAttempts?: number;
}): Promise<LaborActiveSessionPayload> {
  const maxAttempts = input.maxAttempts ?? 8;
  let lastPayload: LaborActiveSessionPayload = { session: null };

  for (let attempt = 0; attempt < maxAttempts; attempt++) {
    if (isLaborOwnerDeparting()) {
      await waitForLaborOwnerDepartureComplete();
    }

    const payload = await input.fetchActiveSession({ page: input.page, domain: input.domain });
    lastPayload = payload;

    const otherPage = activeOnOtherPageFromPayload(payload);
    if (otherPage === "") {
      return payload;
    }

    const shouldDefer = shouldDeferActiveOnOtherConflict({
      activeOnOtherPage: otherPage,
      ownerDeparting: isLaborOwnerDeparting(),
      openSessionPage: input.openSessionPage,
      openSessionTabId: input.openSessionTabId,
      currentTabId: input.currentTabId,
    });
    if (!shouldDefer) {
      return payload;
    }

    await waitForLaborOwnerDepartureComplete();
  }

  return lastPayload;
}
